import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ApiError } from '../../lib/api';
import { useRoles } from '../../hooks/useRoles';
import { useRoleCategories } from '../../hooks/useRoleCategories';
import { useMobilisationsList } from '../../hooks/useMobilisations';
import {
  useTrainingModuleTargets,
  useSetTrainingModuleTargets,
  type TrainingModuleSummary,
} from '../../hooks/useTrainingModules';
import { ModalShell } from '../ui/ModalShell';
import { Button } from '../ui/Button';
import { SHARED_INPUT_CLASS } from './builder/pageMeta';

/**
 * Login roles worth targeting training at, in the order they read. Deliberately
 * not every role: super_admin and the per-station roles would only ever narrow
 * an allocation to a handful of accounts, which the per-person tick does better.
 */
const TARGETABLE_APP_ROLES: { value: string; label: string }[] = [
  { value: 'inductee', label: 'Inductee (new starter)' },
  { value: 'learner', label: 'Learner (existing staff)' },
  { value: 'hr', label: 'HR' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'coordinator', label: 'Coordinator' },
  { value: 'manager', label: 'Manager' },
  { value: 'reception', label: 'Reception' },
  { value: 'admin', label: 'Admin' },
];

/**
 * Allocate a module by facet: trade (job role / category), mobilisation, and
 * login role. Saved targets are reconciled onto matching people by the training
 * sweep, so this is the "make it mandatory for gas engineers" and "inductees on
 * the HCC go-live" control.
 *
 * Facets narrow each other (AND); values inside a facet widen (OR). Leaving a
 * facet empty means "don't care", so one facet on its own behaves exactly as
 * role targeting always has.
 */
export function TargetRolesModal({
  module,
  open,
  onClose,
}: {
  module: TrainingModuleSummary;
  open: boolean;
  onClose: () => void;
}) {
  const roles = useRoles();
  const categories = useRoleCategories();
  const mobilisations = useMobilisationsList();
  const targets = useTrainingModuleTargets(open ? module.id : null);
  const save = useSetTrainingModuleTargets();

  const [roleIds, setRoleIds] = useState<Set<string>>(new Set());
  const [categoryIds, setCategoryIds] = useState<Set<string>>(new Set());
  const [mobilisationIds, setMobilisationIds] = useState<Set<string>>(new Set());
  const [appRoles, setAppRoles] = useState<Set<string>>(new Set());
  const [seeded, setSeeded] = useState(false);
  const [filter, setFilter] = useState('');

  // Seed the selection from the saved targets once they load.
  useEffect(() => {
    if (seeded || !targets.data) return;
    setRoleIds(new Set(targets.data.role_ids));
    setCategoryIds(new Set(targets.data.category_ids));
    setMobilisationIds(new Set(targets.data.mobilisation_ids ?? []));
    setAppRoles(new Set(targets.data.app_roles ?? []));
    setSeeded(true);
  }, [seeded, targets.data]);

  const filteredRoles = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const list = roles.data ?? [];
    return q ? list.filter((r) => r.name.toLowerCase().includes(q)) : list;
  }, [roles.data, filter]);

  function toggle(set: Set<string>, id: string): Set<string> {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  }

  function onSave() {
    save.mutate(
      {
        id: module.id,
        role_ids: [...roleIds],
        category_ids: [...categoryIds],
        mobilisation_ids: [...mobilisationIds],
        app_roles: [...appRoles],
      },
      {
        onSuccess: () => {
          toast.success('Allocation saved');
          onClose();
        },
        onError: (err) =>
          toast.error(err instanceof ApiError ? err.message : 'Could not save allocation'),
      },
    );
  }

  const total = roleIds.size + categoryIds.size + mobilisationIds.size + appRoles.size;

  return (
    <ModalShell open={open} onClose={onClose} titleId="target-roles" maxWidth="max-w-lg">
      <div className="flex max-h-[80vh] flex-col gap-3 p-1">
        <div>
          <h2 id="target-roles" className="text-base font-semibold text-fg">
            Who gets “{module.title}”?
          </h2>
          <p className="mt-0.5 text-sm text-fg-muted">
            Anyone matching <strong>every</strong> section you set gets this as mandatory training,
            now and as people are added later. Leave a section empty to ignore it.
          </p>
        </div>

        {targets.isLoading ? (
          <p className="py-6 text-center text-sm text-fg-muted">Loading…</p>
        ) : (
          <div className="flex-1 space-y-4 overflow-y-auto">
            {(categories.data?.length ?? 0) > 0 ? (
              <div>
                <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-fg-dim">
                  Categories
                </div>
                <div className="space-y-1">
                  {(categories.data ?? []).map((c) => (
                    <label key={c.id} className="flex items-center gap-2 text-sm text-fg">
                      <input
                        type="checkbox"
                        checked={categoryIds.has(c.id)}
                        onChange={() => setCategoryIds((s) => toggle(s, c.id))}
                      />
                      <span>{c.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            ) : null}

            <div>
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-fg-dim">
                  Roles
                </span>
                <input
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Filter roles…"
                  className={`${SHARED_INPUT_CLASS} mt-0 w-40 py-1 text-xs`}
                />
              </div>
              <div className="max-h-56 space-y-1 overflow-y-auto rounded-token-sm border border-line/10 p-2">
                {filteredRoles.length === 0 ? (
                  <p className="px-1 py-2 text-sm text-fg-dim">No roles match.</p>
                ) : (
                  filteredRoles.map((r) => (
                    <label key={r.id} className="flex items-center gap-2 text-sm text-fg">
                      <input
                        type="checkbox"
                        checked={roleIds.has(r.id)}
                        onChange={() => setRoleIds((s) => toggle(s, r.id))}
                      />
                      <span>{r.name}</span>
                    </label>
                  ))
                )}
              </div>
            </div>

            <div>
              <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-fg-dim">
                Mobilisations
              </div>
              {(mobilisations.data ?? []).filter((m) => m.kind === 'mobilisation').length === 0 ? (
                <p className="text-sm text-fg-dim">No mobilisations yet.</p>
              ) : (
                <div className="space-y-1">
                  {(mobilisations.data ?? [])
                    .filter((m) => m.kind === 'mobilisation')
                    .map((m) => (
                      <label key={m.id} className="flex items-center gap-2 text-sm text-fg">
                        <input
                          type="checkbox"
                          checked={mobilisationIds.has(m.id)}
                          onChange={() => setMobilisationIds((s) => toggle(s, m.id))}
                        />
                        <span>{m.name}</span>
                      </label>
                    ))}
                </div>
              )}
            </div>

            <div>
              <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-fg-dim">
                Login roles
              </div>
              <p className="mb-1.5 text-xs text-fg-muted">
                Their access role, not their trade. “Inductee” also covers new starters who have not
                signed in yet.
              </p>
              <div className="space-y-1">
                {TARGETABLE_APP_ROLES.map((r) => (
                  <label key={r.value} className="flex items-center gap-2 text-sm text-fg">
                    <input
                      type="checkbox"
                      checked={appRoles.has(r.value)}
                      onChange={() => setAppRoles((s) => toggle(s, r.value))}
                    />
                    <span>{r.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between gap-2 border-t border-line/10 pt-3">
          <span className="text-xs text-fg-dim">
            {total === 0 ? 'Not allocated' : `${total} selected`}
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" loading={save.isPending} onClick={onSave}>
              Save allocation
            </Button>
          </div>
        </div>
      </div>
    </ModalShell>
  );
}
